import { RefObject, useEffect, useState } from 'react';

export type UseVisibilityOptions = {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  once?: boolean;
};

type UseVisibilityResult = {
  isVisible: boolean;
};

const useVisibility = <T extends Element>(
  target: RefObject<T | null>,
  options: UseVisibilityOptions = {}
): UseVisibilityResult => {
  const [isVisible, setIsVisible] = useState(false);
  const { root = null, rootMargin, threshold, once = false } = options;

  useEffect(() => {
    const element = target?.current;

    if (!element || (once && isVisible)) {
      return undefined;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (!entry) return;

        if (entry.isIntersecting) {
          setIsVisible(true);

          if (once) {
            observer.unobserve(entry.target);
          }
        } else if (!once) {
          setIsVisible(false);
        }
      },
      {
        root,
        rootMargin,
        threshold,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [target, root, rootMargin, threshold, once, isVisible]);

  return { isVisible };
};

export default useVisibility;
